import React, { useMemo, useState } from 'react';
import { Editor } from '@tinymce/tinymce-react';
import { Box, Text } from '@chakra-ui/react';
import { useFormContext } from 'react-hook-form';
import { ISubmissionFormData } from 'src/contexts/FormContext';
import { get } from 'lodash';
import { FormFeedback } from '@morressier/alembic';
import { toSentenceCase, trimLeadingEmptyLines } from 'src/utils/string';
import striptags from 'striptags';

export type RichTextField =
  | 'metadata.title'
  | 'metadata.abstract'
  | 'metadata.englishAbstract'
  | 'metadata.englishTitle'
  | 'metadata.subtitle'
  | 'metadata.englishSubtitle'
  | 'preference.commentForEditor';

interface Props {
  initialValue: string;
  label: string;
  field: RichTextField;
  maxWords?: number;
}

const RichTextSubmission: React.FC<Props> = ({
  initialValue,
  label,
  field,
  maxWords
}) => {
  const form = useFormContext<ISubmissionFormData>();
  const { setValue } = form;

  const error = get(form.formState.errors, field);

  const apiKeyTinymce = import.meta.env.VITE_TINY_MCE_CLIENT_KEY;
  const selfHostedTinymce = import.meta.env.VITE_SELF_HOSTED_TINY_MCE;

  const [wordCount, setWordCount] = useState(0);

  const handleEditorChange = (content: string, editor: any): void => {
    const plainText = editor.getContent({ format: 'text' }).trim();

    const words = plainText.split(/\s+/).filter(Boolean).length;
    setWordCount(words);

    const normalizedContent = trimLeadingEmptyLines(content);

    if (plainText.length <= 0) {
      setValue(field, '', { shouldValidate: true, shouldDirty: true });
      return;
    }

    form.clearErrors(field);

    setValue(field, normalizedContent, {
      shouldValidate: true,
      shouldDirty: true
    });
  };

  const handleEditorBlur = (_evt: any, editor: any): void => {
    const plainText = editor.getContent({ format: 'text' }).trim();
    if (plainText.length <= 0) return;

    const current = form.getValues(field);
    const trimmed = current
      ? trimLeadingEmptyLines(current)
          .replace(/^(<[a-z][a-z0-9]*>)(\s|&nbsp;)+/gi, '$1')
          .replace(/(\s|&nbsp;)+(<\/[a-z][a-z0-9]*>)/gi, '$2')
          .trim()
      : current;
    if (trimmed !== current) {
      setValue(field, trimmed, { shouldValidate: true, shouldDirty: true });
    }
  };

  const labelSentenceCase = toSentenceCase(label);
  const currentValue = form.watch(field) ?? '';

  const currentPlainText = striptags(currentValue)
    .replace(/&nbsp;/gi, ' ')
    .trim();

  const displayedWordCount = useMemo(() => {
    if (wordCount > 0) return wordCount;

    return currentPlainText
      ? currentPlainText.split(/\s+/).filter(Boolean).length
      : 0;
  }, [currentPlainText, wordCount]);

  const maxWordsExceeded =
    typeof maxWords === 'number' && displayedWordCount > maxWords;

  const minLengthMatch =
    typeof error?.message === 'string'
      ? error.message.match(/must NOT have fewer than (\d+) characters/i)
      : null;
  const minLengthLimit = minLengthMatch ? Number(minLengthMatch[1]) : null;

  const validationErrorMessage =
    typeof error?.message === 'string'
      ? error.type === 'richTextMinLength'
        ? currentPlainText.length === 0
          ? `${labelSentenceCase} is required`
          : minLengthLimit
          ? `${labelSentenceCase} must be at least ${minLengthLimit} characters`
          : `${labelSentenceCase} ${error.message}`
        : `${labelSentenceCase} ${error.message}`
      : undefined;

  const errorMessage = maxWordsExceeded
    ? `${labelSentenceCase} cannot exceed ${maxWords} words`
    : validationErrorMessage;

  return (
    <Box width="100%">
      <Box
        width="100%"
        className={error || maxWordsExceeded ? 'editorErrorContainer' : ''}
      >
        <Editor
          id={`editor_${label}`}
          {...(apiKeyTinymce
            ? { apiKey: apiKeyTinymce }
            : { tinymceScriptSrc: selfHostedTinymce })}
          licenseKey={selfHostedTinymce ? 'gpl' : ''}
          value={initialValue}
          {...form.register(field)}
          init={{
            menubar: false,
            statusbar: false,
            paste_as_text: true,
            plugins: ['math', 'autoresize', 'wordcount'],
            toolbar: 'undo redo | formatselect | math',
            entity_encoding: 'raw'
          }}
          onEditorChange={handleEditorChange}
          onBlur={handleEditorBlur}
        />
      </Box>

      {typeof maxWords === 'number' && (
        <Text
          mt={2}
          textAlign="right"
          color={maxWordsExceeded ? 'red.500' : 'gray.500'}
        >
          {displayedWordCount} / {maxWords} words
        </Text>
      )}

      {errorMessage && (
        <FormFeedback variant="error">{errorMessage}</FormFeedback>
      )}
    </Box>
  );
};

export default RichTextSubmission;
